/**
 * Simulador de reajuste (Rede e preços, #242 item 5): estudo para negociar com a
 * rede. Um % por família de procedimento em cada lado, aplicado sobre o volume
 * real do período escolhido na tela. Mostra valor atual x simulado, a diferença no
 * período e em 12 meses, e a comparação dos dois lados antes e depois.
 *
 * Cenários salvos (gestor da Inteligência de Custo) guardam período, lados e %.
 * Exportar PDF imprime só o simulador, com o carimbo "GNI - Inovação".
 * % por procedimento (item a item): #249.
 * Backend: /resultado-plano/utilizacao/rede/simulacao e /cenarios.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Printer, Save, Trash2, FolderOpen } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../services/api";
import InfoTip from "../../components/InfoTip";

const BASE = "/resultado-plano/utilizacao/rede";
const T = { ink: "#2c3e50", inkSoft: "#7f8c8d", line: "#e8e8e8", teal: "#20a490", tealSoft: "#e8f6f4", risco: "#c0392b", riscoSoft: "#fdecea", verde: "#1f8a4c", azul: "#3498db", ambarSoft: "#fef5e7" };
const fmtR = (v) => (v === null || v === undefined ? "—" : `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtS = (v) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : v < 0 ? "-" : ""}R$ ${Math.abs(Number(v)).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const mesBR = (m) => (m ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : "");
const erroDe = (e, p) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : p; };
const cor = (v) => (v > 0 ? T.risco : v < 0 ? T.verde : T.ink);   // custo a mais = vermelho
const TH = { padding: "7px 8px", fontWeight: 600, fontSize: 12, color: T.inkSoft, borderBottom: `1px solid ${T.line}`, textAlign: "right" };
const TD = { padding: "6px 8px", fontSize: 13, color: T.ink, borderBottom: `1px solid ${T.line}`, textAlign: "right", whiteSpace: "nowrap" };
const campo = { fontSize: 12, padding: "5px 8px", border: `1px solid ${T.line}`, borderRadius: 6, background: "#fff" };
const btn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${T.line}`, background: "#fff", color: T.ink };

const TIP = {
  tela: "Aplica um reajuste (para mais ou para menos) em cada família de procedimento, em cada lado, sobre o volume real do período escolhido acima. Serve para preparar a proposta de negociação com o prestador. O reajuste incide sobre o valor pago ao prestador; a coparticipação fica como está. Use o período por data do atendimento para o % incidir sobre o preço vigente. Limite: a simulação usa o volume do período; se o volume mudar, o efeito muda.",
  doze: "A diferença do período levada para 12 meses (diferença ÷ número de meses × 12). É uma projeção simples: supõe o mesmo volume por mês.",
  comparacao: "A mesma conta da tela (o volume do primeiro lado aos preços de cada lado, só nos procedimentos que os dois fazem), antes e depois do reajuste.",
  familias: "Os grupos de procedimento do ERP juntados em 6 famílias. Diárias e taxas é a Tabela TUSS 18; Materiais e OPME, a 19; Medicamentos, a 20. O que não entra nas outras vai para Procedimentos e exames. O % por procedimento, item a item, vem depois (#249).",
};
const CSS_PRINT = `
.sim-carimbo{display:none}
@media print{
  @page{size:A4 landscape;margin:10mm}
  html,body{background:#fff!important}
  body *{visibility:hidden}
  body *:not(.sim-imprime):not(.sim-imprime *){background:transparent!important;box-shadow:none!important}
  .sim-imprime,.sim-imprime *{visibility:visible}
  .sim-imprime{position:absolute;left:0;top:0;width:100%}
  .sim-imprime .so-tela{display:none!important}
  .sim-imprime *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .sim-carimbo{display:inline-block}
}`;

export default function SimuladorReajuste({ params, ra, rb, gestor, mes, aoAbrirCenario }) {
  const [familias, setFamilias] = useState([]);
  const [reaj, setReaj] = useState({ A: {}, B: {} });
  const [d, setD] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [erro, setErro] = useState(null);
  const [cenarios, setCenarios] = useState([]);
  const [aberto, setAberto] = useState(null);          // cenário carregado
  const [nome, setNome] = useState("");
  const [obs, setObs] = useState("");
  const [comparaCom, setComparaCom] = useState("");
  const [outro, setOutro] = useState(null);
  const [hora, setHora] = useState("");
  const ref = useRef(null);

  const carregarCenarios = () => api.get(`${BASE}/cenarios`).then((r) => setCenarios(r.data)).catch(() => setCenarios([]));
  useEffect(() => {
    api.get(`${BASE}/simulacao/familias`).then((r) => setFamilias(r.data)).catch(() => {});
    carregarCenarios();
  }, []);

  const corpo = useMemo(() => ({
    mes: params.mes, mes_ate: params.mes_ate, tp: params.tp, min_qt: params.min_qt, lado_a: params.lado_a, lado_b: params.lado_b,
    sem_copart: !!params.sem_copart, base: params.base || "competencia",
  }), [params]);
  const limpo = (r) => ({ A: Object.fromEntries(Object.entries(r.A).filter(([, v]) => Number(v))), B: Object.fromEntries(Object.entries(r.B).filter(([, v]) => Number(v))) });

  // recalcula meio segundo depois da última mudança (o servidor guarda a leitura do período)
  useEffect(() => {
    if (!corpo.mes) return undefined;
    let vivo = true;
    const h = setTimeout(() => {
      setCalculando(true); setErro(null);
      api.post(`${BASE}/simulacao`, { ...corpo, reajustes: limpo(reaj) })
        .then((r) => vivo && setD(r.data)).catch((e) => vivo && setErro(erroDe(e, "Não foi possível simular.")))
        .finally(() => vivo && setCalculando(false));
    }, 500);
    return () => { vivo = false; clearTimeout(h); };
  }, [corpo, reaj]); // eslint-disable-line

  // cenário para comparar: mesmo período e lados da tela, com os % dele
  useEffect(() => {
    const c = cenarios.find((x) => String(x.id) === comparaCom);
    if (!c) { setOutro(null); return undefined; }
    let vivo = true;
    api.post(`${BASE}/simulacao`, { ...corpo, reajustes: c.reajustes }).then((r) => vivo && setOutro({ nome: c.nome, d: r.data })).catch(() => vivo && setOutro(null));
    return () => { vivo = false; };
  }, [comparaCom, corpo, cenarios]);

  const pct = (lado, f) => reaj[lado][f] ?? "";
  const mudar = (lado, f, v) => {
    const n = v === "" || v === "-" ? v : Math.max(-90, Math.min(200, Number(String(v).replace(",", "."))));
    setReaj((r) => ({ ...r, [lado]: { ...r[lado], [f]: Number.isNaN(n) ? "" : n } }));
  };
  const abrir = (c) => {
    setAberto(c); setNome(c.nome); setObs(c.observacao || "");
    setReaj({ A: { ...(c.reajustes.A || {}) }, B: { ...(c.reajustes.B || {}) } });
    aoAbrirCenario(c.parametros);
    if (c.parametros.mes && c.parametros.mes !== mes) toast(`Cenário montado para ${mesBR(c.parametros.mes)}: escolha essa competência acima para ver o mesmo período.`, { duration: 6000 });
  };
  const salvar = async (novo) => {
    try {
      const body = { nome, observacao: obs, parametros: corpo, reajustes: limpo(reaj) };
      const r = aberto && !novo ? await api.put(`${BASE}/cenarios/${aberto.id}`, body) : await api.post(`${BASE}/cenarios`, body);
      toast.success("Cenário salvo");
      const lista = await api.get(`${BASE}/cenarios`); setCenarios(lista.data);
      setAberto(lista.data.find((c) => c.id === r.data.id) || null);
    } catch (e) { toast.error(erroDe(e, "Não foi possível salvar.")); }
  };
  const apagar = async () => {
    if (!aberto || !window.confirm(`Apagar o cenário "${aberto.nome}"?`)) return;
    try { await api.delete(`${BASE}/cenarios/${aberto.id}`); toast.success("Cenário apagado"); setAberto(null); setNome(""); setObs(""); carregarCenarios(); }
    catch (e) { toast.error(erroDe(e, "Não foi possível apagar.")); }
  };
  const imprimir = () => {
    setHora(new Date().toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }));
    setTimeout(() => window.print(), 50);
  };

  const ladoCel = (f, lado) => {
    const x = d?.familias.find((y) => y.codigo === f.codigo)?.[lado];
    return (
      <>
        <td style={TD}>{fmtR(x?.atual)}</td>
        <td style={TD}>
          <input className="so-tela" value={pct(lado, f.codigo)} onChange={(e) => mudar(lado, f.codigo, e.target.value)} inputMode="decimal" placeholder="0"
                 aria-label={`Reajuste ${f.rotulo} ${lado === "A" ? ra : rb}`} style={{ ...campo, width: 64, textAlign: "right" }} />
          <span className="so-print" style={{ display: "none" }}>{x?.pct ? `${x.pct > 0 ? "+" : ""}${x.pct}%` : "0%"}</span> %
        </td>
        <td style={{ ...TD, fontWeight: 700 }}>{fmtR(x?.simulado)}</td>
        <td style={{ ...TD, color: cor(x?.dif) }}>{x?.dif ? fmtS(x.dif) : "—"}</td>
      </>
    );
  };
  const tot = d?.total;
  const antes = d?.comparacao?.antes, depois = d?.comparacao?.depois;
  const frase = (ag) => (ag ? (ag.economia_de_a >= 0 ? `${ra} sai ${fmtR(ag.economia_de_a)} mais barato` : `${ra} sai ${fmtR(-ag.economia_de_a)} mais caro`) : "—");

  return (
    <div style={{ marginTop: 14, border: `1px solid ${T.line}`, borderRadius: 12, padding: 12, background: "#fff" }} className="sim-imprime" ref={ref}>
      <style>{CSS_PRINT + "@media print{.sim-imprime .so-print{display:inline!important}}"}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <b style={{ fontSize: 14, color: T.ink }}>Simular reajuste para negociação</b>
        <InfoTip w={420} texto={TIP.tela} />
        {calculando && <span style={{ fontSize: 11, color: T.inkSoft }}>calculando…</span>}
        <span className="sim-carimbo" style={{ fontSize: 11, fontWeight: 700, color: "#167a6b", border: "1.5px solid #167a6b", borderRadius: 6, padding: "2px 8px" }}>GNI - Inovação · AURA · exportado em {hora}</span>
        <div style={{ flex: 1 }} />
        <button className="so-tela" style={btn} onClick={imprimir} disabled={!d}><Printer size={13} /> Exportar PDF</button>
      </div>
      <div style={{ fontSize: 12, color: T.inkSoft, margin: "4px 0 8px" }}>
        {d ? <>Volume real de {d.meses} mês(es), {d.base === "atendimento" ? "pela data do atendimento" : "pela competência"}{params.mes_ate ? ` (${mesBR(params.mes)} a ${mesBR(params.mes_ate)})` : ` (${mesBR(params.mes)})`}. {aberto && <b>Cenário: {aberto.nome}.</b>}</> : "Montando a simulação…"}
        {aberto?.observacao && <div style={{ marginTop: 2 }}>{aberto.observacao}</div>}
      </div>
      {erro && <div style={{ color: T.risco, fontSize: 12 }}>{erro}</div>}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr><th style={{ ...TH, textAlign: "left" }} /><th colSpan={4} style={{ ...TH, textAlign: "center", color: T.teal }}>{ra}</th><th colSpan={4} style={{ ...TH, textAlign: "center", color: T.azul }}>{rb}</th></tr>
            <tr>
              <th style={{ ...TH, textAlign: "left" }}><span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>Família <InfoTip w={340} texto={TIP.familias} /></span></th>
              {["A", "B"].map((l) => <React.Fragment key={l}><th style={TH}>Valor atual</th><th style={TH}>Reajuste</th><th style={TH}>Simulado</th><th style={TH}>Diferença</th></React.Fragment>)}
            </tr>
          </thead>
          <tbody>
            {familias.map((f) => (
              <tr key={f.codigo}>
                <td style={{ ...TD, textAlign: "left" }} title={f.explica}>{f.rotulo}</td>
                {ladoCel(f, "A")}{ladoCel(f, "B")}
              </tr>))}
            {tot && (
              <tr style={{ background: T.tealSoft }}>
                <td style={{ ...TD, textAlign: "left", fontWeight: 800 }}>Total</td>
                {["A", "B"].map((l) => <React.Fragment key={l}><td style={{ ...TD, fontWeight: 800 }}>{fmtR(tot[l].atual)}</td><td style={{ ...TD, fontWeight: 800 }}>{tot[l].pct ? `${tot[l].pct > 0 ? "+" : ""}${tot[l].pct.toLocaleString("pt-BR")}%` : "0%"}</td><td style={{ ...TD, fontWeight: 800 }}>{fmtR(tot[l].simulado)}</td><td style={{ ...TD, fontWeight: 800, color: cor(tot[l].dif) }}>{tot[l].dif ? fmtS(tot[l].dif) : "—"}</td></React.Fragment>)}
              </tr>)}
          </tbody>
        </table>
      </div>

      {tot && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 10, marginTop: 10 }}>
          {[["A", ra], ["B", rb]].map(([l, r]) => (
            <div key={l} style={{ border: `1px solid ${T.line}`, borderRadius: 10, padding: "8px 10px" }}>
              <div style={{ fontSize: 11.5, color: T.inkSoft }}>{r}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: cor(tot[l].dif) }}>{tot[l].dif ? fmtS(tot[l].dif) : "sem reajuste"} <span style={{ fontSize: 12, fontWeight: 400, color: T.inkSoft }}>no período</span></div>
              <div style={{ fontSize: 13, color: cor(tot[l].dif_12m), display: "flex", alignItems: "center", gap: 4 }}>{tot[l].dif_12m ? fmtS(tot[l].dif_12m) : "—"} em 12 meses <InfoTip w={280} texto={TIP.doze} /></div>
            </div>))}
          <div style={{ border: `1px solid ${T.line}`, borderRadius: 10, padding: "8px 10px", background: T.ambarSoft }}>
            <div style={{ fontSize: 11.5, color: T.inkSoft, display: "flex", alignItems: "center", gap: 4 }}>Comparação dos dois lados <InfoTip w={300} texto={TIP.comparacao} /></div>
            <div style={{ fontSize: 13 }}>Hoje: <b>{frase(antes)}</b></div>
            <div style={{ fontSize: 13 }}>Com o reajuste: <b>{frase(depois)}</b></div>
          </div>
        </div>
      )}

      {outro && tot && (
        <div style={{ marginTop: 10, fontSize: 12.5, border: `1px dashed ${T.line}`, borderRadius: 10, padding: "8px 10px" }}>
          <b>Comparando com "{outro.nome}":</b>{" "}
          {ra}: {fmtS(outro.d.total.A.dif)} no período ({fmtS(outro.d.total.A.dif_12m)} em 12 meses) contra {fmtS(tot.A.dif)} neste;{" "}
          {rb}: {fmtS(outro.d.total.B.dif)} contra {fmtS(tot.B.dif)}. Com o outro cenário: {frase(outro.d.comparacao.depois)}.
        </div>
      )}

      <div className="so-tela" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.line}` }}>
        <FolderOpen size={14} color={T.inkSoft} />
        <select value={aberto?.id || ""} onChange={(e) => { const c = cenarios.find((x) => String(x.id) === e.target.value); if (c) abrir(c); }} style={campo} aria-label="Abrir cenário">
          <option value="">{cenarios.length ? "Abrir cenário salvo…" : "Nenhum cenário salvo"}</option>
          {cenarios.map((c) => <option key={c.id} value={c.id}>{c.nome} ({c.atualizado_por || c.criado_por})</option>)}
        </select>
        <select value={comparaCom} onChange={(e) => setComparaCom(e.target.value)} style={campo} aria-label="Comparar com cenário">
          <option value="">Comparar com outro cenário…</option>
          {cenarios.filter((c) => c.id !== aberto?.id).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <button style={btn} onClick={() => { setReaj({ A: {}, B: {} }); setAberto(null); setNome(""); setObs(""); }}>Zerar</button>
        {gestor && <>
          <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder='Nome (ex.: "Proposta Baía Sul 2027")' maxLength={120} style={{ ...campo, minWidth: 230 }} aria-label="Nome do cenário" />
          <input value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Observação (opcional)" maxLength={2000} style={{ ...campo, minWidth: 200 }} aria-label="Observação do cenário" />
          <button style={btn} onClick={() => salvar(false)} disabled={!nome.trim()}><Save size={13} /> {aberto ? "Salvar alterações" : "Salvar cenário"}</button>
          {aberto && <button style={btn} onClick={() => salvar(true)} disabled={!nome.trim()}>Salvar como novo</button>}
          {aberto && <button style={{ ...btn, color: T.risco }} onClick={apagar}><Trash2 size={13} /> Apagar</button>}
        </>}
        {!gestor && <span style={{ fontSize: 11.5, color: T.inkSoft }}>Salvar cenário é do gestor da Inteligência de Custo.</span>}
      </div>
    </div>
  );
}
